// src/App.js - Updated to automatically redirect based on user role
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext"; // Import SocketProvider
import { AnalysisProvider } from "./context/AnalysisContext"; // Import AnalysisProvider
import { LanguageProvider } from "./context/LanguageContext"; // Import LanguageProvider
import { ReadOnlyProvider } from "./context/ReadOnlyContext"; // Import ReadOnlyProvider for viewer mode
import { ChatProvider } from "./context/ChatContext"; // Import ChatProvider for chatbot
import './i18n/index'; // Initialize i18n
import PrivateRoute from "./components/common/PrivateRoute";
import AccessDenied from "./components/common/AccessDenied";
import LoginForm from "./components/auth/LoginForm";
import ErrorBoundary from "./components/ErrorBoundary"; // Import ErrorBoundary
// Token refresh notification removed

// Layouts
import AdminLayout from "./components/layout/AdminLayout";
import ProviderLayout from "./components/layout/ProviderLayout";

// Pages SuperAdmin
import SuperAdminDashboard from "./pages/admin/SuperAdminDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import CompanyManagement from "./pages/admin/CompanyManagement";
import UserManagement from "./pages/admin/UserManagement";
import SuperAdminUserManagement from "./pages/admin/SuperAdminUserManagement";
import CompanyUserManagement from "./pages/admin/CompanyUserManagement";
import ActivityLogs from "./pages/admin/ActivityLogs";
import AiConsumption from "./pages/admin/AiConsumption";
import SubscriptionManagement from "./pages/admin/SubscriptionManagement";
import ConfigurationsPage from "./pages/admin/ConfigurationsPage";
import ContactMessages from "./pages/admin/ContactMessages";

// Pages Admin (Company-scoped)
import AdminUserManagement from "./pages/admin/AdminUserManagement";
import AdminActivityLogs from "./pages/admin/AdminActivityLogs";
import AdminSubscription from "./pages/admin/AdminSubscription";
import PortfolioManagement from "./pages/admin/PortfolioManagement";
import CompanyAnalyses from "./pages/admin/CompanyAnalyses";

// Pages Provider
import ProviderDashboard from "./pages/provider/ProviderDashboard";
import ProviderUserManagement from "./pages/provider/UserManagement";
import AnalysisManagement from "./pages/provider/AnalysisManagement";
import CompanyActivities from "./pages/provider/CompanyActivities";
import UserActivities from "./pages/provider/UserActivities";

// Subscription renewal page
import SubscriptionRenewal from "./pages/SubscriptionRenewal";
import HomePage from "./pages/HomePage";

// Subscription guard
import SubscriptionGuard from "./components/common/SubscriptionGuard";

// Application principale existante
import EbiosRMApp from "./EbiosRMApp";
import AuthWrapper from "./AuthWrapper";
import { useAuth } from "./context/AuthContext";

// New component to handle role-based redirects
const RoleBasedRedirect = () => {
  const { user } = useAuth();

  // If user has superadmin role, redirect to superadmin dashboard
  if (user && user.role === 'superadmin') {
    return <Navigate to="/superadmin/dashboard" replace />;
  }

  // If user has admin (provider) role, redirect to provider dashboard
  if (user && user.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // If user has simpleuser (analyst) role, redirect to the application
  if (user && user.role === 'simpleuser') {
    return <Navigate to="/app" replace />;
  }

  // If user has viewer role, redirect to the application (read-only mode)
  if (user && user.role === 'viewer') {
    return <Navigate to="/app" replace />;
  }

  // If user role is not recognized or user is not authenticated
  return <Navigate to="/login" replace />;
};

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <ChatProvider>
            <LanguageProvider>
              <BrowserRouter>
              <AnalysisProvider> {/* Inside BrowserRouter to use useNavigate */}
                <ReadOnlyProvider> {/* Provides read-only mode for viewer accounts */}
                  {/* Token refresh notification removed */}
                  <Routes>
                    {/* Route de connexion accessible à tous */}
                    <Route path="/login" element={<LoginForm />} />

                    {/* Page d'accès refusé */}
                    <Route path="/access-denied" element={<AccessDenied />} />

                    {/* Public landing */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/home" element={<HomePage />} />

                    {/* Authenticated root — role-based dashboard */}
                    <Route
                      path="/dashboard"
                      element={
                        <PrivateRoute>
                          <RoleBasedRedirect />
                        </PrivateRoute>
                      }
                    />

                    {/* Routes Super Admin - accessibles uniquement par les superadmins */}
                    <Route
                      path="/superadmin/dashboard"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <SuperAdminDashboard />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/superadmin/companies"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <CompanyManagement />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/superadmin/users"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <SuperAdminUserManagement />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/superadmin/activities"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <ActivityLogs />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/superadmin/ai-consumption"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <AiConsumption />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/superadmin/subscriptions"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <SubscriptionManagement />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/superadmin/configurations"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <ConfigurationsPage />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/superadmin/analyses"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <CompanyAnalyses />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/superadmin/companies/:id/users"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <CompanyUserManagement />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/superadmin/messages"
                      element={
                        <PrivateRoute roles="superadmin">
                          <AdminLayout>
                            <ContactMessages />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />

                    {/* Routes Provider - accessibles uniquement par les providers (admin) */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <PrivateRoute roles="admin">
                          <SubscriptionGuard>
                            <AdminLayout>
                              <AdminDashboard />
                            </AdminLayout>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <PrivateRoute roles="admin">
                          <SubscriptionGuard>
                            <AdminLayout>
                              <AdminUserManagement />
                            </AdminLayout>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin/analyses"
                      element={
                        <PrivateRoute roles="admin">
                          <SubscriptionGuard>
                            <AdminLayout>
                              <CompanyAnalyses />
                            </AdminLayout>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin/activities"
                      element={
                        <PrivateRoute roles="admin">
                          <SubscriptionGuard>
                            <AdminLayout>
                              <AdminActivityLogs />
                            </AdminLayout>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin/subscription"
                      element={
                        <PrivateRoute roles="admin">
                          <AdminLayout>
                            <AdminSubscription />
                          </AdminLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin/portfolios"
                      element={
                        <PrivateRoute roles="admin">
                          <SubscriptionGuard>
                            <AdminLayout>
                              <PortfolioManagement />
                            </AdminLayout>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />
                    {/* New route for user-specific activities */}
                    <Route
                      path="/admin/users/:userId/logs"
                      element={
                        <PrivateRoute roles="admin">
                          <SubscriptionGuard>
                            <ProviderLayout>
                              <UserActivities />
                            </ProviderLayout>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />

                    {/* Subscription renewal page */}
                    <Route
                      path="/subscription-renewal"
                      element={
                        <PrivateRoute roles={['admin', 'simpleuser']}>
                          <SubscriptionRenewal />
                        </PrivateRoute>
                      }
                    />

                    {/* Application principale - accessible par tous utilisateurs authentifiés */}
                    {/* Viewers can access but will be in read-only mode via ReadOnlyProvider */}
                    <Route
                      path="/app"
                      element={
                        <PrivateRoute roles={['superadmin', 'admin', 'simpleuser', 'viewer']}>
                          <SubscriptionGuard>
                            <AuthWrapper>
                              <EbiosRMApp />
                            </AuthWrapper>
                          </SubscriptionGuard>
                        </PrivateRoute>
                      }
                    />

                    {/* Route par défaut - retour landing */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </ReadOnlyProvider>
                </AnalysisProvider>
              </BrowserRouter>
            </LanguageProvider>
          </ChatProvider>
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;